import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Table,
    Form,
    Button,
    Card,
    Alert,
    Spinner,
    InputGroup,
    Badge,
    Pagination
} from 'react-bootstrap';
import { clientsAPI, getProducts, getBranches } from '../utils/api';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import {
    BsFileEarmarkPdf,
    BsPerson,
    BsBox,
    BsXCircle,
    BsCalendar,
    BsChevronLeft,
    BsChevronRight,
    BsArrowUp,
    BsArrowDown,
    BsArrowDownUp
} from 'react-icons/bs';
import '../css/OverallReport.css';

const OverallSalesReport = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 25,
        totalPages: 0
    });

    // Add state for products dropdown
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        fromDate: '',
        toDate: '',
        clientName: '',
        productName: '',
        branchName: ''
    });

    // Statistics
    const [statistics, setStatistics] = useState({
        totalTransactions: 0,
        totalAmount: 0,
        totalPaid: 0,
        totalBalance: 0
    });

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: 'transaction_date', direction: 'desc' });

    // Fetch products on component mount
    useEffect(() => {
        fetchProducts();
        fetchBranches();
    }, []);

    // Fetch transactions on filter or page change
    useEffect(() => {
        fetchTransactions(currentPage);
    }, [filters, currentPage]);

    // Function to fetch products from database
    const fetchProducts = async () => {
        setLoadingProducts(true);
        try {
            const response = await getProducts();
            // Assuming response structure - adjust based on your API
            if (response && response.data) {
                setProducts(response.data);
            } else if (Array.isArray(response)) {
                setProducts(response);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
        } finally {
            setLoadingProducts(false);
        }
    };

    const fetchBranches = async () => {
        setLoadingBranches(true);
        try {
            const data = await getBranches();
            setBranches(data);
        } catch (err) {
            console.error('Error fetching branches:', err);
        } finally {
            setLoadingBranches(false);
        }
    };

    const fetchTransactions = async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                ...filters,
                page,
                limit: 25
            };
            const response = await clientsAPI.getOverallSales(params);
            if (response.success) {
                setTransactions(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
                if (response.statistics) {
                    setStatistics({
                        totalTransactions: response.pagination.total,
                        totalAmount: response.statistics.totalAmount,
                        totalPaid: response.statistics.totalPaid,
                        totalBalance: response.statistics.totalBalance
                    });
                }
            } else {
                setError('Failed to fetch transactions');
            }
        } catch (err) {
            setError(err.message || 'An error occurred while fetching transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setCurrentPage(1); // Reset to first page on filter change
    };

    const clearFilters = () => {
        setFilters({
            fromDate: '',
            toDate: '',
            clientName: '',
            productName: '',
            branchName: ''
        });
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    // Sorting Logic
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortData = (data, config) => {
        if (!config.key) return data;

        return [...data].sort((a, b) => {
            let valA, valB;

            // Handle transaction-level vs item-level keys
            const itemKeys = ['product_name', 'quantity', 'unit_price', 'discount_percent', 'gst_amount', 'total'];

            if (itemKeys.includes(config.key)) {
                // Sort by the first item's value if multiple items exist for a transaction
                valA = a.items && a.items.length > 0 ? a.items[0][config.key] : 0;
                valB = b.items && b.items.length > 0 ? b.items[0][config.key] : 0;
            } else {
                valA = a[config.key];
                valB = b[config.key];
            }

            // Date parsing for sorting
            if (config.key === 'transaction_date') {
                valA = new Date(valA || 0);
                valB = new Date(valB || 0);
            }

            // Numeric comparison
            if (typeof valA === 'number' && typeof valB === 'number') {
                return config.direction === 'asc' ? valA - valB : valB - valA;
            }

            // String comparison
            valA = String(valA || '').toLowerCase();
            valB = String(valB || '').toLowerCase();

            if (valA < valB) return config.direction === 'asc' ? -1 : 1;
            if (valA > valB) return config.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key !== key) return <BsArrowDownUp className="ms-1 text-muted opacity-50" style={{ fontSize: '0.7rem' }} />;
        return sortConfig.direction === 'asc'
            ? <BsArrowUp className="ms-1 text-primary" />
            : <BsArrowDown className="ms-1 text-primary" />;
    };

    const sortedTransactions = sortData(transactions, sortConfig);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const exportToPDF = async () => {
        try {
            setLoading(true);
            const params = {
                ...filters,
                exportAll: true
            };
            const response = await clientsAPI.getOverallSales(params);

            // Handle both response object structures
            const allTransactions = response.data || response;

            const doc = new jsPDF('landscape');

            // Add title
            doc.setFontSize(18);
            doc.text('Overall Sales Report', 14, 15);

            // Add date range if applied
            doc.setFontSize(10);
            let dateRangeText = 'Date Range: ';
            if (filters.fromDate && filters.toDate) {
                dateRangeText += `${formatDate(filters.fromDate)} - ${formatDate(filters.toDate)}`;
            } else if (filters.fromDate) {
                dateRangeText += `From ${formatDate(filters.fromDate)}`;
            } else if (filters.toDate) {
                dateRangeText += `Until ${formatDate(filters.toDate)}`;
            } else {
                dateRangeText += 'All Time';
            }
            doc.text(dateRangeText, 14, 22);

            // Add filters
            let yPos = 28;
            if (filters.clientName) {
                doc.text(`Client: ${filters.clientName}`, 14, yPos);
                yPos += 6;
            }
            if (filters.productName) {
                doc.text(`Product: ${filters.productName}`, 14, yPos);
                yPos += 6;
            }

            // Add generation date
            const today = new Date();
            doc.text(`Generated on: ${today.toLocaleDateString('en-IN')} ${today.toLocaleTimeString('en-IN')}`, 14, yPos);
            doc.text(`Total Records: ${allTransactions.length}`, 14, yPos + 6);
            yPos += 12;

            // Helper function to format numbers without currency symbol
            const formatNumber = (amount) => {
                if (!amount && amount !== 0) return '0.00';
                return Number(amount).toLocaleString('en-IN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            };

            // Prepare table data
            const tableData = [];
            allTransactions.forEach(transaction => {
                if (transaction.items && transaction.items.length > 0) {
                    transaction.items.forEach((item, index) => {
                        tableData.push([
                            index === 0 ? formatDate(transaction.transaction_date) : '',
                            index === 0 ? transaction.bill_no || 'N/A' : '',
                            index === 0 ? transaction.client_name || 'N/A' : '',
                            index === 0 ? transaction.branch_name || 'Main Branch' : '',
                            index === 0 ? (transaction.transaction_type || 'N/A').toUpperCase() : '',
                            item.product_name || 'N/A',
                            item.quantity || 0,
                            formatNumber(item.unit_price),
                            `${item.discount_percent || 0}%`,
                            formatNumber(item.gst_amount || 0),
                            formatNumber(item.total || 0),
                            index === 0 ? formatNumber(transaction.amount_paid) : '',
                            index === 0 ? (transaction.account_paid || '') : '',
                            index === 0 ? formatNumber(transaction.balance) : ''
                        ]);
                    });
                } else {
                    tableData.push([
                        formatDate(transaction.transaction_date),
                        transaction.bill_no || 'N/A',
                        transaction.client_name || 'N/A',
                        transaction.branch_name || 'Main Branch',
                        (transaction.transaction_type || 'N/A').toUpperCase(),
                        'No items',
                        '-',
                        '-',
                        '-',
                        '-',
                        formatNumber(transaction.grand_total),
                        formatNumber(transaction.amount_paid),
                        (transaction.account_paid || ''),
                        formatNumber(transaction.balance)
                    ]);
                }
            });

            autoTable(doc, {
                startY: yPos + 5,
                head: [[
                    'Date', 'Bill No', 'Client', 'Branch', 'Type', 'Product', 'Qty',
                    'Unit Price', 'Disc %', 'GST', 'Total', 'Paid', 'Acct Paid', 'Balance'
                ]],
                body: tableData,
                theme: 'striped',
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 8 },
                styles: { fontSize: 7, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 18 },
                    1: { cellWidth: 15 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 20 },
                    4: { cellWidth: 15 },
                    5: { cellWidth: 35 },
                    6: { cellWidth: 8 },
                    7: { cellWidth: 15 },
                    8: { cellWidth: 10 },
                    9: { cellWidth: 12 },
                    10: { cellWidth: 18 },
                    11: { cellWidth: 15 },
                    12: { cellWidth: 15 },
                    13: { cellWidth: 15 }
                }
            });

            // Save the PDF
            doc.save(`Overall_Sales_Report_${new Date().toISOString().split('T')[0]}.pdf`);
            setLoading(false);
        } catch (err) {
            console.error('Export error:', err);
            setError('Failed to export PDF. Please try again.');
            setLoading(false);
        }
    };

    // Render Pagination
    const renderPagination = () => {
        if (pagination.totalPages <= 1) return null;

        let items = [];
        const { totalPages, page } = pagination;

        let startPage = Math.max(1, page - 2);
        let endPage = Math.min(totalPages, startPage + 4);

        if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
        }

        items.push(
            <Pagination.First key="first" onClick={() => handlePageChange(1)} disabled={page === 1} />
        );
        items.push(
            <Pagination.Prev key="prev" onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
        );

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === page}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next key="next" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} />
        );
        items.push(
            <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
        );

        return (
            <div className="d-flex justify-content-center mt-4 mb-2">
                <Pagination className="pagination-premium">{items}</Pagination>
            </div>
        );
    };

    return (
        <Container fluid className="py-4 report-container">
            <Row className="mb-4 align-items-center">
                <Col>
                    <h2 className="report-title">Overall Sales Report</h2>
                    <p className="text-muted">A comprehensive overview of all sales activities across clients</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="premium-success"
                        onClick={exportToPDF}
                        disabled={transactions.length === 0}
                        className="export-btn shadow-sm"
                    >
                        <BsFileEarmarkPdf className="me-2" />
                        Export PDF
                    </Button>
                </Col>
            </Row>

            {/* Filters Card */}
            <Card className="filter-card mb-4 border-0 shadow-sm">
                <Card.Body>
                    <div className="d-flex align-items-center mb-3">
                        <div className="filter-icon me-2">
                            <BsCalendar />
                        </div>
                        <h5 className="mb-0">Filters</h5>
                    </div>
                    <Row className="g-2">
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase fw-bold text-muted">From Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    className="filter-input"
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase fw-bold text-muted">To Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    className="filter-input"
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase fw-bold text-muted">Client Name</Form.Label>
                                <InputGroup className="filter-input-group">
                                    <InputGroup.Text className="bg-white border-end-0">
                                        <BsPerson className="text-secondary" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by client"
                                        className="border-start-0 filter-input"
                                        value={filters.clientName}
                                        onChange={(e) => handleFilterChange('clientName', e.target.value)}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase fw-bold text-muted">Branch Name</Form.Label>
                                <Form.Select
                                    className="filter-input"
                                    value={filters.branchName}
                                    onChange={(e) => handleFilterChange('branchName', e.target.value)}
                                    disabled={loadingBranches}
                                >
                                    <option value="">All Branches</option>
                                    {branches.map((branch, index) => (
                                        <option key={branch.id || index} value={branch.name}>
                                            {branch.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase fw-bold text-muted">Product</Form.Label>
                                <Form.Select
                                    className="filter-input"
                                    value={filters.productName}
                                    onChange={(e) => handleFilterChange('productName', e.target.value)}
                                    disabled={loadingProducts}
                                >
                                    <option value="">All Products</option>
                                    {products.map((product, index) => (
                                        <option key={product.id || index} value={product.name || product.product_name}>
                                            {product.name || product.product_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={1} className="d-flex align-items-end">
                            <Button
                                variant="outline-danger"
                                onClick={clearFilters}
                                className="mb-3 w-100 clear-btn border-0"
                                title="Clear Filters"
                            >
                                <BsXCircle />
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Statistics Cards */}
            <Row className="mb-4 g-3">
                {[
                    { label: 'Total Transactions', value: statistics.totalTransactions, icon: 'bi-list-check', bg: 'primary' },
                    { label: 'Total Sales', value: formatCurrency(statistics.totalAmount), icon: 'bi-graph-up-arrow', bg: 'success' },
                    { label: 'Total Paid', value: formatCurrency(statistics.totalPaid), icon: 'bi-check-circle', bg: 'info' },
                    { label: 'Total Sales Balance', value: formatCurrency(statistics.totalBalance), icon: 'bi-wallet-fill', bg: 'danger' }
                ].map((stat, i) => (
                    <Col key={i} xs={12} sm={6} lg={3}>
                        <Card className={`stat-card border-0 shadow-sm bg-premium-${stat.bg} text-white h-100`}>
                            <Card.Body className="d-flex align-items-center">
                                <div className="stat-icon-wrapper me-3 flex-shrink-0">
                                    <i className={`bi ${stat.icon} fs-4`}></i>
                                </div>
                                <div className="overflow-hidden">
                                    <div className="small text-white-50 text-uppercase text-truncate">{stat.label}</div>
                                    <h4 className="mb-0 fw-bold fs-5 text-nowrap">{stat.value}</h4>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Transactions Table Card */}
            <Card className="report-table-card border-0 shadow-sm overflow-hidden">
                <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold">Transaction Breakdown</h5>
                    <div className="d-flex align-items-center">
                        <Badge bg="light" text="dark" className="px-3 py-2 me-2">
                            Showing {transactions.length} of {pagination.total} results
                        </Badge>
                        <Badge bg="primary" className="px-3 py-2">
                            Page {currentPage} of {pagination.totalPages}
                        </Badge>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Loading transactions...</p>
                        </div>
                    ) : error ? (
                        <Alert variant="danger" className="m-3 text-center">{error}</Alert>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="premium-table mb-0">
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSort('transaction_date')} className="sortable-header">Date {renderSortArrow('transaction_date')}</th>
                                            <th onClick={() => handleSort('bill_no')} className="sortable-header">Bill No {renderSortArrow('bill_no')}</th>
                                            <th onClick={() => handleSort('client_name')} className="sortable-header">Client Name {renderSortArrow('client_name')}</th>
                                            <th onClick={() => handleSort('branch_name')} className="sortable-header">Branch {renderSortArrow('branch_name')}</th>
                                            <th onClick={() => handleSort('transaction_type')} className="sortable-header">Type {renderSortArrow('transaction_type')}</th>
                                            <th onClick={() => handleSort('product_name')} className="sortable-header">Product {renderSortArrow('product_name')}</th>
                                            <th onClick={() => handleSort('quantity')} className="sortable-header text-center">Qty {renderSortArrow('quantity')}</th>
                                            <th onClick={() => handleSort('unit_price')} className="sortable-header text-end">Price {renderSortArrow('unit_price')}</th>
                                            <th onClick={() => handleSort('discount_percent')} className="sortable-header text-end">Disc % {renderSortArrow('discount_percent')}</th>
                                            <th onClick={() => handleSort('gst_amount')} className="sortable-header text-end">GST {renderSortArrow('gst_amount')}</th>
                                            <th onClick={() => handleSort('total')} className="sortable-header text-end">Total {renderSortArrow('total')}</th>
                                            <th onClick={() => handleSort('amount_paid')} className="sortable-header text-end">Paid {renderSortArrow('amount_paid')}</th>
                                            <th onClick={() => handleSort('account_paid')} className="sortable-header text-end">Acct {renderSortArrow('account_paid')}</th>
                                            <th onClick={() => handleSort('balance')} className="sortable-header text-end border-end">Balance {renderSortArrow('balance')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedTransactions.length === 0 ? (
                                            <tr>
                                                <td colSpan="14" className="text-center py-5 text-muted">
                                                    No transactions found with the current filters
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedTransactions.map((transaction) => {
                                                const displayItems = transaction.items || [];

                                                return displayItems.length > 0 ? (
                                                    displayItems.map((item, index) => (
                                                        <tr key={`${transaction.sale_transaction_id}-${item.item_id || index}`} className={index === 0 ? 'transaction-start' : 'transaction-item'}>
                                                            {index === 0 && (
                                                                <>
                                                                    <td rowSpan={displayItems.length} className="fw-medium text-nowrap">
                                                                        {formatDate(transaction.transaction_date)}
                                                                    </td>
                                                                    <td rowSpan={displayItems.length} className="text-primary fw-bold">
                                                                        {transaction.bill_no || 'N/A'}
                                                                    </td>
                                                                    <td rowSpan={displayItems.length} className="fw-medium">
                                                                        {transaction.client_name}
                                                                    </td>
                                                                    <td rowSpan={displayItems.length} className="text-muted small">
                                                                        {transaction.branch_name}
                                                                    </td>
                                                                    <td rowSpan={displayItems.length}>
                                                                        <Badge className={`badge-premium ${transaction.transaction_type?.toLowerCase() === 'sale' ? 'bg-success' : transaction.transaction_type?.toLowerCase() === 'return' ? 'bg-danger' : 'bg-primary'}`}>
                                                                            {transaction.transaction_type}
                                                                        </Badge>
                                                                    </td>
                                                                </>
                                                            )}
                                                            <td className="text-muted">{item.product_name}</td>
                                                            <td className="text-center fw-bold">{item.quantity}</td>
                                                            <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                            <td className="text-end">{item.discount_percent || 0}%</td>
                                                            <td className="text-end text-muted small">{formatCurrency(item.gst_amount)}</td>
                                                            <td className="text-end fw-bold">{formatCurrency(item.total)}</td>
                                                            {index === 0 && (
                                                                <>
                                                                    <td rowSpan={displayItems.length} className="text-end text-success fw-medium">
                                                                        {formatCurrency(transaction.amount_paid)}
                                                                    </td>
                                                                    <td rowSpan={displayItems.length} className="text-end text-muted small">
                                                                        {transaction.account_paid}
                                                                    </td>
                                                                    <td rowSpan={displayItems.length} className="text-end text-danger fw-bold">
                                                                        {formatCurrency(transaction.balance)}
                                                                    </td>
                                                                </>
                                                            )}
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr key={transaction.sale_transaction_id}>
                                                        <td className="fw-medium">{formatDate(transaction.transaction_date)}</td>
                                                        <td className="text-primary fw-bold">{transaction.bill_no || 'N/A'}</td>
                                                        <td className="fw-medium">{transaction.client_name}</td>
                                                        <td className="text-muted small">{transaction.branch_name}</td>
                                                        <td>
                                                            <Badge className={`badge-premium ${transaction.transaction_type?.toLowerCase() === 'sale' ? 'bg-success' : transaction.transaction_type?.toLowerCase() === 'return' ? 'bg-danger' : 'bg-primary'}`}>
                                                                {transaction.transaction_type}
                                                            </Badge>
                                                        </td>
                                                        <td colSpan="5" className="text-center text-muted italic">No items recorded</td>
                                                        <td className="text-end fw-bold">{formatCurrency(transaction.grand_total)}</td>
                                                        <td className="text-end text-success fw-medium">{formatCurrency(transaction.amount_paid)}</td>
                                                        <td className="text-end text-muted small">{transaction.account_paid}</td>
                                                        <td className="text-end text-danger fw-bold">{formatCurrency(transaction.balance)}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            {renderPagination()}
                        </>
                    )}
                </Card.Body>
                <Card.Footer className="bg-light py-3">
                    <Row className="text-end fw-bold">
                        <Col md={{ span: 4, offset: 4 }}>
                            <span className="text-uppercase small text-muted me-2">Grand Total Sum:</span>
                            <span className="fs-5">{formatCurrency(statistics.totalAmount)}</span>
                        </Col>
                        <Col md={4}>
                            <span className="text-uppercase small text-muted me-2">Global Balance:</span>
                            <span className="fs-5 text-danger">{formatCurrency(statistics.totalBalance)}</span>
                        </Col>
                    </Row>
                </Card.Footer>
            </Card>
        </Container>
    );
};

export default OverallSalesReport;
